/**
 * Card das bicicletas USADAS.
 *
 * Porte de `bicicletas_peniel_prototipo.html` linhas 228-241 (e 260-274 para o
 * estado vendida):
 *   bg-gray-50 border border-gray-100 rounded-xl hover:shadow-lg
 *   imagem h-48 com opacity-90 → 100 no hover, badge "USADA" mini,
 *   título text-lg, preço text-xl, botão gray-900 rounded-lg py-2 text-sm
 *
 * São dois componentes separados (e não um com `variante`) porque os dois
 * designs divergem em ~12 slots de classe — raio, altura da imagem, padding,
 * forma do badge, cor e tamanho do botão, escala do texto. Um componente único
 * com ternários viraria sopa ilegível.
 */
import { Link } from 'react-router-dom'
import type { ProdutoResumo } from '@peniel/shared'
import { SIZES, ehNegociavel } from '@peniel/shared'

import { ImagemResponsiva } from '@/shared/ui/ImagemResponsiva.jsx'
import { BadgeCondicao, BadgeVendida } from './BadgeCondicao.jsx'
import { PrecoProduto } from './PrecoProduto.jsx'
import { BotaoWhatsAppProduto } from './BotaoWhatsApp.jsx'

export function CardProdutoUsado({
  produto,
  prioridade = false,
}: {
  produto: ProdutoResumo
  prioridade?: boolean
}) {
  const vendida = produto.status === 'VENDIDA'
  const negociavel = ehNegociavel(produto.status)

  return (
    <li className="group flex flex-col overflow-hidden rounded-xl border border-gray-100 bg-gray-50 transition-shadow hover:shadow-lg">
      <div className="relative h-48 shrink-0">
        <BadgeCondicao condicao={produto.condicao} variante="usada" />
        {vendida && <BadgeVendida />}

        <Link to={`/bicicletas/${produto.slug}`} className="block size-full">
          <ImagemResponsiva
            imagem={produto.imagem}
            alt={produto.nome}
            sizes={SIZES.cardUsada}
            className="size-full"
            prioridade={prioridade}
            zoomNoHover={!vendida}
            esmaecida={vendida}
            classNameImg={vendida ? undefined : 'opacity-90 group-hover:opacity-100'}
          />
        </Link>
      </div>

      <div className="flex grow flex-col p-5">
        <h3 className="mb-1 text-lg font-bold text-gray-900">
          <Link to={`/bicicletas/${produto.slug}`} className="hover:text-brand-700">
            {produto.nome}
          </Link>
        </h3>

        {produto.resumo && (
          <p className="mb-3 grow text-sm text-gray-500">{produto.resumo}</p>
        )}

        <PrecoProduto
          centavos={produto.precoCentavos}
          riscado={vendida}
          className={`mb-3 text-xl font-bold ${vendida ? 'text-gray-500' : 'text-gray-900'}`}
        />

        {negociavel ? (
          <BotaoWhatsAppProduto
            produto={produto}
            variante="escura"
            forma="caixa-sm"
            tamanho="sm"
            rotulo="Tenho Interesse"
          />
        ) : (
          <span className="flex min-h-11 w-full items-center justify-center rounded-lg bg-gray-200 px-3 text-sm font-semibold text-gray-500">
            Vendida — indisponível
          </span>
        )}
      </div>
    </li>
  )
}
